import React, { useCallback, useMemo, useRef } from "react";
import { useField } from "react-final-form";
import { Delete, Edit } from "@mui/icons-material";
import {
  ColDef,
  EditStrategyType,
  GridOptions,
  ICellRendererParams,
  RowEditingStoppedEvent,
  ValueGetterParams,
  ValueSetterParams,
} from "ag-grid-community";
import { isEqual } from "lodash";

import { Attribute, LayerClassification, Maybe } from "fond/types";
import { AgGrid } from "fond/widgets";
import IconButtonCellRenderer from "fond/widgets/AgGrid/cellRenderer/IconButton";

import CellRenderer from "./CellRenderer";
import { STOPS_PER_CLASSIFICATION } from "./Classify";
import { Empty } from "./Empty";
import { getRangeValue, useDefaultStyle } from "./helper";

interface IProps {
  colors: string[];
  gradientColors: boolean;
  attributeType?: Attribute["Type"];
  attributeId?: string;
}

// ValueGetter wrapper for AG Grid
const rangeValueGetter = (expressionIndex: number) => (params: ValueGetterParams<LayerClassification>) => {
  return getRangeValue(params.data, expressionIndex);
};

// ValueSetter wrapper for AG Grid
const rangeValueSetter = (expressionIndex: number) => (params: ValueSetterParams<LayerClassification>) => {
  const filterConfig = params.data.FilterConfiguration;
  if (filterConfig.Type === "group" && filterConfig.Expressions && filterConfig.Expressions.length > expressionIndex) {
    const newValueParsed = parseFloat(params.newValue);
    if (Number.isNaN(newValueParsed) || newValueParsed === params.oldValue) {
      return false;
    }
    filterConfig.Expressions[expressionIndex].ValueKey = newValueParsed;
    return true;
  }
  return false;
};

const ClassifyData: React.FC<IProps> = ({ attributeId, attributeType, colors, gradientColors }: IProps) => {
  const {
    input: { onChange, value },
  } = useField("layerClassifications");
  const editedRowRef = useRef<LayerClassification | null>(null);
  const defaultStyle = useDefaultStyle();

  const getRowColors = useCallback(
    (index: number, type: Maybe<string>) => {
      if (gradientColors && type !== "other") {
        // Calculate color index accounting for multiple stops per classification
        let colorIndex = 0;
        for (let i = 0; i < index; i += 1) {
          if (value[i]?.FilterConfiguration.Type !== "other") {
            colorIndex += STOPS_PER_CLASSIFICATION;
          }
        }

        // Return multiple colors for this classification
        const startIdx = colorIndex;
        const endIdx = colorIndex + STOPS_PER_CLASSIFICATION;
        return colors.slice(startIdx, endIdx + 1).length > 0 ? colors.slice(startIdx, endIdx + 1) : [colors[0] || "#ccc"];
      }

      // For non-gradient, calculate the single color index
      let colorIndex = 0;
      for (let i = 0; i < index; i += 1) {
        if (value[i]?.FilterConfiguration.Type !== "other") {
          colorIndex += 1;
        }
      }

      return [colors[colorIndex] || colors[0] || "#ccc"];
    },
    [colors, gradientColors, value]
  );

  const columns: ColDef[] = useMemo(() => {
    const baseColumns: ColDef[] = [
      {
        field: "Style",
        headerName: "Style",
        width: 100,
        cellRenderer: CellRenderer,
        cellRendererParams: { style: defaultStyle, getRowColors, isGradient: gradientColors, attributeId },
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        sortable: false,
      },
      {
        field: "Label",
        headerName: "Label",
        flex: 2,
        cellDataType: "text",
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        resizable: false,
        sortable: false,
        editable: true,
      },
    ];

    // Only add Min/Max columns if attributeType is REAL / INTEGER
    if (attributeType === "REAL" || attributeType === "INTEGER") {
      baseColumns.push(
        {
          headerName: "Min",
          width: 125,
          cellDataType: "text",
          suppressHeaderMenuButton: true,
          suppressMovable: true,
          resizable: false,
          sortable: false,
          editable: (params) => {
            const filterConfig = params.data.FilterConfiguration;
            return filterConfig.Type !== "other";
          },
          valueGetter: rangeValueGetter(0),
          valueSetter: rangeValueSetter(0),
        },
        {
          headerName: "Max",
          width: 125,
          cellDataType: "text",
          suppressHeaderMenuButton: true,
          suppressMovable: true,
          resizable: false,
          sortable: false,
          editable: (params) => {
            const filterConfig = params.data.FilterConfiguration;
            return filterConfig.Type !== "other";
          },
          valueGetter: rangeValueGetter(1),
          valueSetter: rangeValueSetter(1),
        }
      );
    }
    return [
      ...baseColumns,
      {
        headerName: "",
        width: 50,
        cellRenderer: IconButtonCellRenderer,
        cellRendererParams: (params: ICellRendererParams<LayerClassification>) => ({
          icon: Edit,
          iconButtonProps: {
            onClick: () => {
              // Start editing the Label column for this row
              if (params.node && params.node.rowIndex !== null) {
                params.api.startEditingCell({
                  rowIndex: params.node.rowIndex,
                  colKey: "Label",
                });
              }
            },
            color: "primary",
            sx: { "& svg": { fontSize: "1rem" } },
          },
        }),
      },
      {
        headerName: "",
        width: 50,
        cellRenderer: IconButtonCellRenderer,
        cellRendererParams: (params: ICellRendererParams<LayerClassification>) => ({
          icon: Delete,
          iconButtonProps: {
            onClick: () => {
              // Remove row from form's layerClassifications field only
              const updatedRows = value.filter((item: LayerClassification) => item !== params.data);
              onChange(updatedRows);
            },
            color: "primary",
            sx: { "& svg": { fontSize: "1rem" } },
          },
        }),
      },
    ];
  }, [attributeId, attributeType, defaultStyle, getRowColors, gradientColors, onChange, value]);

  const handleOnRowEditingStopped = useCallback((event: RowEditingStoppedEvent<LayerClassification>) => {
    if (!event.data || !editedRowRef.current) return;

    const updatedData = event.data;
    const originalData = editedRowRef.current;

    const minValue = getRangeValue(updatedData, 0);
    const maxValue = getRangeValue(updatedData, 1);
    const originalMaxValue = getRangeValue(originalData, 1);

    // Update current row's label if the user didn't change it manually (i.e. it's still in "min - max" format)
    if (isEqual(originalData.Label, updatedData.Label)) {
      event.node.setDataValue("Label", `${minValue} - ${maxValue}`);
    }

    // If max value changed, update next row's min value
    if (maxValue !== originalMaxValue && event.node.rowIndex !== null) {
      const nextRowIndex = event.node.rowIndex + 1;
      const nextRow = event.api.getDisplayedRowAtIndex(nextRowIndex);
      if (nextRow && nextRow.data) {
        const nextFilterConfig = nextRow.data.FilterConfiguration;
        if (nextFilterConfig.Type === "group" && nextFilterConfig.Expressions && nextFilterConfig.Expressions.length > 0) {
          // Update next row's min value to match current row's max
          nextFilterConfig.Expressions[0].ValueKey = parseFloat(maxValue as string);
          // Also update next row's label
          const nextMaxValue = getRangeValue(nextRow.data, 1);
          nextRow.setDataValue("Label", `${maxValue} - ${nextMaxValue}`);
        }
      }
    }
  }, []);

  const gridOptions: GridOptions = useMemo(
    () => ({
      animateRows: true,
      editType: "fullRow" as EditStrategyType,
      rowGroupPanelShow: "never" as const,
      sideBar: false,
      domLayout: "normal" as const,
      noRowsOverlayComponent: Empty,
      getRowId: (params) => params.data.ID,
      defaultColDef: {
        lockPosition: true,
        resizable: false,
        suppressHeaderMenuButton: true,
      },
      onRowEditingStopped: handleOnRowEditingStopped,
      onRowEditingStarted: (event) => {
        editedRowRef.current = structuredClone(event.data); // Store a copy of the original row data when editing starts
      },
    }),
    [handleOnRowEditingStopped]
  );

  return <AgGrid columnDefs={columns} rowData={value} gridOptions={gridOptions} size="compact" variant="outlined" />;
};

export default ClassifyData;
